import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  AppSettings,
  PersistedDocument,
  DEFAULT_SETTINGS,
  DEFAULT_DOCUMENT,
  loadPersistentState,
  savePersistentState,
  CURRENT_SCHEMA_VERSION,
} from './state';
import { createDocumentModel } from './core/text';
import { HomeView } from './ui/HomeView';
import { PrompterView } from './ui/PrompterView';
import { SettingsDrawer } from './ui/SettingsDrawer';
import { DiagnosticsModal } from './ui/DiagnosticsModal';
import { ModelManager } from './voice/model_manager';
import { VoiceController } from './voice/voice_controller';
import { MoonshineAdapter } from './voice/moonshine_adapter';
import { AlignmentEngine } from './core/align/engine';
import { FollowController } from './core/scroll/follow_controller';
import { ScrollEngine } from './core/scroll/engine';
import { LayoutIndex } from './core/geometry/layout_index';

export const App: React.FC = () => {
  const [currentView, setCurrentView] = useState<'home' | 'prompter'>('home');
  const [settings, setSettings] = useState<AppSettings>(DEFAULT_SETTINGS);
  const [documentState, setDocumentState] = useState<PersistedDocument>(DEFAULT_DOCUMENT);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isDiagnosticsOpen, setIsDiagnosticsOpen] = useState(false);

  // Voice Follow subsystem singletons
  const modelManagerRef = useRef<ModelManager>(new ModelManager());
  const voiceControllerRef = useRef<VoiceController | null>(null);

  // Document model derived pure from raw script
  const doc = useMemo(() => {
    return createDocumentModel(documentState.script);
  }, [documentState.script]);

  // 1. Initial Load from IndexedDB
  useEffect(() => {
    loadPersistentState().then((stored) => {
      setSettings(stored.settings);
      setDocumentState(stored.document);
      setIsLoaded(true);

      // Initialize Voice Controller
      const adapter = new MoonshineAdapter();
      const aligner = new AlignmentEngine(createDocumentModel(stored.document.script).tokens);
      const followController = new FollowController();
      const scrollEngine = new ScrollEngine();
      const layoutIndex = new LayoutIndex();

      const controller = new VoiceController(
        adapter,
        aligner,
        followController,
        scrollEngine,
        layoutIndex
      );
      voiceControllerRef.current = controller;
    });
  }, []);

  // Update Aligner tokens when script changes
  useEffect(() => {
    voiceControllerRef.current?.setTokens(doc.tokens);
  }, [doc]);

  // 2. Debounced Autosave to IndexedDB
  const saveTimeoutRef = useRef<any>(null);

  useEffect(() => {
    if (!isLoaded) return;

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(() => {
      savePersistentState({
        schemaVersion: CURRENT_SCHEMA_VERSION,
        settings,
        document: documentState,
      });
    }, 500);

    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, [settings, documentState, isLoaded]);

  // Flush on visibilitychange or pagehide
  useEffect(() => {
    const handleFlush = () => {
      if (isLoaded) {
        savePersistentState({
          schemaVersion: CURRENT_SCHEMA_VERSION,
          settings,
          document: documentState,
        });
      }
    };

    window.addEventListener('visibilitychange', handleFlush);
    window.addEventListener('pagehide', handleFlush);
    return () => {
      window.removeEventListener('visibilitychange', handleFlush);
      window.removeEventListener('pagehide', handleFlush);
    };
  }, [settings, documentState, isLoaded]);

  // Handlers
  const handleScriptChange = (newScript: string) => {
    setDocumentState((prev) => ({
      ...prev,
      script: newScript,
      updatedAt: Date.now(),
    }));
  };

  const handleClearScript = () => {
    setDocumentState((prev) => ({
      script: '',
      lastClearedScript: prev.script,
      updatedAt: Date.now(),
    }));
  };

  const handleUndoClearScript = () => {
    if (!documentState.lastClearedScript) return;
    setDocumentState((prev) => ({
      script: prev.lastClearedScript,
      lastClearedScript: '',
      updatedAt: Date.now(),
    }));
  };

  const handleUpdateSettings = (partial: Partial<AppSettings>) => {
    setSettings((prev) => ({
      ...prev,
      ...partial,
    }));
  };

  const handleResetSettings = () => {
    setSettings({ ...DEFAULT_SETTINGS });
  };

  if (!isLoaded) {
    return <div className="w-full h-full bg-black flex items-center justify-center text-neutral-500 font-mono text-xs">Loading TP...</div>;
  }

  return (
    <div className="w-full h-full bg-black text-white relative overflow-hidden font-sans">
      {currentView === 'home' ? (
        <HomeView
          scriptText={documentState.script}
          settings={settings}
          canUndoClear={!!documentState.lastClearedScript}
          onScriptChange={handleScriptChange}
          onClearScript={handleClearScript}
          onUndoClearScript={handleUndoClearScript}
          onUpdateSettings={handleUpdateSettings}
          onOpenSettings={() => setIsSettingsOpen(true)}
          onStartPrompter={() => setCurrentView('prompter')}
          voiceController={voiceControllerRef.current}
        />
      ) : (
        voiceControllerRef.current && (
          <PrompterView
            doc={doc}
            settings={settings}
            onUpdateSettings={handleUpdateSettings}
            onResetSettings={handleResetSettings}
            onBackToEditor={() => setCurrentView('home')}
            modelManager={modelManagerRef.current}
            voiceController={voiceControllerRef.current}
          />
        )
      )}

      {/* Global Settings Drawer on Home */}
      <SettingsDrawer
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        settings={settings}
        onUpdateSettings={handleUpdateSettings}
        onResetSettings={handleResetSettings}
        modelManager={modelManagerRef.current}
        onOpenDiagnostics={() => setIsDiagnosticsOpen(true)}
      />

      {/* Global Diagnostics Modal */}
      <DiagnosticsModal
        isOpen={isDiagnosticsOpen}
        onClose={() => setIsDiagnosticsOpen(false)}
        voiceState={voiceControllerRef.current?.getState() ?? null}
        scrollState={{
          y: 0,
          maxY: 0,
          targetSpeed: settings.speed,
          currentSpeed: 0,
          playState: 'paused',
          controlSource: settings.modePreference,
          clampedFrameCount: 0,
        }}
      />
    </div>
  );
};
